import { useRef } from 'react';
import type { RelativeFile } from '@/types/common.ts';
import type { ManualDatasetCreateResponse } from './types.ts';

type UploadOperation<Result> = {
  identity: string;
  files: RelativeFile[];
  prepare: () => Promise<ManualDatasetCreateResponse>;
  upload: (prepared: ManualDatasetCreateResponse) => Promise<void>;
  commit: (prepared: ManualDatasetCreateResponse) => Promise<Result>;
};

/**
 * Keep a prepared upload through retries in this mounted form. Once the bytes
 * are uploaded, a lost commit response must retry only the idempotent commit:
 * re-uploading could modify data that the server has already sealed.
 */
export default function useStaticUploadOperation<Result>() {
  const pending = useRef<{
    identity: string;
    files: RelativeFile[];
    prepared?: ManualDatasetCreateResponse;
    uploaded: boolean;
  } | null>(null);

  return async ({ identity, files, prepare, upload, commit }: UploadOperation<Result>) => {
    const previous = pending.current;
    if (
      !previous ||
      previous.identity !== identity ||
      previous.files.length !== files.length ||
      previous.files.some(
        (entry, index) => entry.path !== files[index].path || entry.file !== files[index].file,
      )
    ) {
      pending.current = { identity, files: files.map((entry) => ({ ...entry })), uploaded: false };
    }
    const operation = pending.current!;
    operation.prepared ??= await prepare();
    if (!operation.uploaded) {
      await upload(operation.prepared);
      operation.uploaded = true;
    }
    const result = await commit(operation.prepared);
    if (pending.current === operation) pending.current = null;
    return result;
  };
}
